/**
 * Enterprise Account Screen — Multi-tenant ClientMaster CRUD
 * Table: WebApp.ClientMaster (ERPMASTERDB)
 * SP: WebApp.SP_ClientMasterInsert (Flag=10)
 * Pattern: Custom Grid+Form (tabbed) connecting to /api/tenant/clients
 */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Save, Grid3X3, XCircle, Search, Users } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import DynamicFieldRenderer from '../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';

const enterNav = (e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); const f = e.target.closest('form'); if (!f) return; const els = Array.from(f.querySelectorAll('input:not([disabled]):not([readonly]),select:not([disabled])')); const i = els.indexOf(e.target); if (i >= 0 && i < els.length - 1) els[i + 1].focus(); } };

const F = memo(({ label, value, onChange, required, disabled, type = 'text', testId }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control">
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="erp-input" data-testid={testId} onKeyDown={enterNav} />
    </div>
  </div>
));

const S = memo(({ label, value, onChange, options, disabled, testId, required }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control">
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="erp-input erp-select" data-testid={testId} onKeyDown={enterNav}>
        <option value="">--Select--</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  </div>
));

// ═══════════════════════ GRID ═══════════════════════
const ENT_COLUMNS = [
  { field: 'ClientId', header: 'Client ID' },
  { field: 'ClientName', header: 'Client Name' },
  { field: 'CompName', header: 'Company' },
  { field: 'EmailId', header: 'Email' },
  { field: 'PhoneNo1', header: 'Phone' },
  { field: 'Status', header: 'Status' },
  { field: 'CStatus', header: 'Cloud Status' },
  { field: 'Country', header: 'Country' },
  { field: 'MClientId', header: 'Provider' },
];

const EnterpriseGrid = ({ token, onAddNew, onEdit }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      const r = await fetch(`${API_URL}/api/tenant/clients?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.success) { setData(j.data || []); setTotal(j.total || 0); }
    } catch (e) { erpToast.error('Failed to load enterprises'); }
    setLoading(false);
  }, [token, page, pageSize]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleVoid = async (row) => {
    if (!window.confirm(`Void enterprise ${row.ClientId}?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/tenant/clients/${row.ClientId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.success) { erpToast.success('Enterprise voided'); loadData(); } else { erpToast.error(j.error || 'Void failed'); }
    } catch { erpToast.error('Void failed'); }
  };

  return (
    <ERPAdvancedGrid
      title="Enterprise Accounts"
      columns={ENT_COLUMNS}
      data={data}
      total={total}
      idField="ClientId"
      nameField="ClientName"
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      filters={filters}
      onFilterChange={(f) => { setFilters(f); setPage(1); }}
      loading={loading}
      onAddNew={onAddNew}
      onEdit={onEdit}
      onVoid={handleVoid}
      testId="enterprise"
      exportFileName="EnterpriseAccounts"
      cellRenderer={(field, value) => {
        if (field === 'Status') return <span className={`erp-status-badge ${value === 'Active' ? 'active' : 'inactive'}`}>{value}</span>;
        if (field === 'CStatus') return <span className={`erp-status-badge ${value === 'Live' ? 'active' : 'pending'}`}>{value}</span>;
        return null;
      }}
    />
  );
};

// ═══════════════════════ FORM ═══════════════════════
const EnterpriseForm = ({ token, editData, mode, onGridView, onSaved }) => {
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const [tab, setTab] = useState('company');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({});
  const [dynValues, setDynValues] = useState({});
  const [childUsers, setChildUsers] = useState([]);

  // Lookups
  const [providers, setProviders] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [packages, setPackages] = useState([]);

  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const isSuperAdmin = userRole === 'SUPER_ADMIN' || userRole === 'admin' || userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR';
  const hdrs = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const set = (n, v) => setForm(p => ({ ...p, [n]: v }));

  // Load lookups
  useEffect(() => {
    if (!token) return;
    const load = async (url, setter) => { try { const r = await fetch(url, { headers: hdrs }); const j = await r.json(); if (j.success) setter(j.data || []); } catch {} };
    load(`${API_URL}/api/tenant/lookup/providers`, setProviders);
    load(`${API_URL}/api/tenant/lookup/industries`, setIndustries);
    load(`${API_URL}/api/tenant/lookup/countries`, setCountries);
    load(`${API_URL}/api/tenant/lookup/currencies`, setCurrencies);
  }, [token]);

  // Load states when country changes
  useEffect(() => {
    if (!token || !form.Country) { setStates([]); return; }
    const load = async () => {
      try { const r = await fetch(`${API_URL}/api/tenant/lookup/states?countryid=${form.Country}`, { headers: hdrs }); const j = await r.json(); if (j.success) setStates(j.data || []); } catch {}
    };
    load();
  }, [token, form.Country]);

  // Load cities when state changes
  useEffect(() => {
    if (!token || !form.State) { setCities([]); return; }
    const load = async () => {
      try { const r = await fetch(`${API_URL}/api/tenant/lookup/cities?stateid=${form.State}`, { headers: hdrs }); const j = await r.json(); if (j.success) setCities(j.data || []); } catch {}
    };
    load();
  }, [token, form.State]);

  // Load packages when provider changes
  useEffect(() => {
    if (!token || !form.MClientId) { setPackages([]); return; }
    const load = async () => {
      try { const r = await fetch(`${API_URL}/api/tenant/lookup/packages?mclientid=${form.MClientId}`, { headers: hdrs }); const j = await r.json(); if (j.success) setPackages(j.data || []); } catch {}
    };
    load();
  }, [token, form.MClientId]);

  // Load child users for edit/view
  useEffect(() => {
    if (!token || !editData?.ClientId) return;
    const load = async () => {
      try { const r = await fetch(`${API_URL}/api/tenant/clients/${editData.ClientId}/users`, { headers: hdrs }); const j = await r.json(); if (j.success) setChildUsers(j.data || []); } catch {}
    };
    load();
  }, [token, editData]);

  // Initialize form
  useEffect(() => {
    if (editData) {
      setForm({ ...editData });
    } else {
      setForm({ ClientId: '', ClientName: '', CompName: '', Address1: '', City: '', Pincode: '', State: '', Country: 'IND', Currency: '76', PhoneNo1: '', PhoneNo2: '', EmailId: '', Password: '', NoOfDevices: '5', SQLServerId: '', SQLDBName: '', SQLUserName: '', SQLPassword: '', SQLSchemaName: '', TradeLicenceNo: '', TRNNo: '', TRNSTATUS: 'NO', Packages: '', Industry: '', MClientId: 'C10002', Status: 'InActive', CStatus: 'Pending', paymentStatus: 'I' });
    }
  }, [editData]);

  const handleSave = async () => {
    if (!form.ClientName?.trim()) { setError('Client Name is required'); return; }
    if (!form.EmailId?.trim()) { setError('Email is required'); return; }
    if (!form.PhoneNo1?.trim()) { setError('Phone No is required'); return; }
    if (!isEdit && !form.Password?.trim()) { setError('Password is required for new account'); return; }
    setSaving(true); setError('');
    try {
      const url = isEdit ? `${API_URL}/api/tenant/clients/${form.ClientId}` : `${API_URL}/api/tenant/clients`;
      const r = await fetch(url, { method: isEdit ? 'PUT' : 'POST', headers: hdrs, body: JSON.stringify(form) });
      const j = await r.json();
      if (j.success) {
        erpToast.success(isEdit ? 'Enterprise updated!' : `Enterprise created! ID: ${j.data?.ClientId}`);
        onSaved();
      } else {
        setError(j.error || 'Save failed');
        erpToast.error(j.error || 'Save failed');
      }
    } catch (e) { setError('Save failed'); erpToast.error('Save failed'); }
    setSaving(false);
  };

  const tabs = [
    { id: 'company', label: 'Company Info' },
    { id: 'address', label: 'Address & Contact' },
    { id: 'sql', label: 'SQL Connection' },
    { id: 'subscription', label: 'Subscription' },
    { id: 'users', label: 'Users' },
  ];

  return (
    <div className="erp-module" data-testid="enterprise-form">
      <div className="erp-screen-header">
        {onBack ? (<button onClick={onBack} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Enterprise Account</span></button>) : (<span className="erp-screen-title">Enterprise Account</span>)}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11 }}>
          {form.ClientId ? <span>ID: <strong style={{ fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)' }}>{form.ClientId}</strong></span>
          : <span style={{ color: 'var(--erp-warning)', fontFamily: 'var(--erp-font-mono)' }}>New: Auto-generated</span>}
          {form.Status && <span className={`erp-status-badge ${form.Status === 'Active' ? 'active' : 'inactive'}`}>{form.Status}</span>}
          {form.CStatus && <span className={`erp-status-badge ${form.CStatus === 'Live' ? 'active' : 'pending'}`}>{form.CStatus}</span>}
        </div>
        <div className="erp-screen-actions">
          {!isView && <button data-testid="enterprise-save-btn" className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving}><Save size={13} /> {saving ? 'Saving...' : (isEdit ? 'Update' : 'Save')}</button>}
          <button data-testid="enterprise-gridview-btn" className="erp-btn erp-btn-ghost" onClick={onGridView}><Grid3X3 size={13} /> Gridview</button>
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={() => { setForm({ ClientId: '', ClientName: '', CompName: '', Address1: '', City: '', Pincode: '', State: '', Country: 'IND', Currency: '76', PhoneNo1: '', PhoneNo2: '', EmailId: '', Password: '', NoOfDevices: '5', SQLServerId: '', SQLDBName: '', SQLUserName: '', SQLPassword: '', SQLSchemaName: '', TradeLicenceNo: '', TRNNo: '', TRNSTATUS: 'NO', Packages: '', Industry: '', MClientId: 'C10002', Status: 'InActive', CStatus: 'Pending', paymentStatus: 'I' }); setError(''); }}><XCircle size={13} /> Clear</button>}
        </div>
      </div>
      {error && <div className="erp-alert-error">{error}</div>}
      <div className="erp-tabs" data-testid="enterprise-tabs">
        {tabs.map(t => (
          <button key={t.id} className={`erp-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}>
            {t.id === 'users' && <Users size={12} />} {t.label}
          </button>
        ))}
      </div>
      <form style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }} onSubmit={(e) => e.preventDefault()}>
        {tab === 'company' && (
          <div className="erp-form-card"><div className="erp-form-section">
            <div className="erp-form-3col">
              <div>
                <F label="Client ID" value={form.ClientId} onChange={() => {}} disabled testId="ent-clientid" />
                <F label="Client Name" value={form.ClientName} onChange={(v) => set('ClientName', v)} required disabled={isView} testId="ent-clientname" />
                <F label="Company Name" value={form.CompName} onChange={(v) => set('CompName', v)} required disabled={isView} testId="ent-compname" />
                <F label="Email" value={form.EmailId} onChange={(v) => set('EmailId', v)} required disabled={isView} testId="ent-email" type="email" />
                {!isEdit && <F label="Password" value={form.Password} onChange={(v) => set('Password', v)} required disabled={isView} testId="ent-password" type="password" />}
              </div>
              <div>
                <S label="Provider" value={form.MClientId} onChange={(v) => set('MClientId', v)} required disabled={isView} testId="ent-provider"
                  options={providers.map(p => ({ value: p.ClientId, label: `${p.ClientId} — ${p.ClientName}` }))} />
                <S label="Industry" value={form.Industry} onChange={(v) => set('Industry', v)} disabled={isView} testId="ent-industry"
                  options={industries.map(i => ({ value: i.IndustryId, label: i.IndustryName }))} />
                <F label="Trade License No" value={form.TradeLicenceNo} onChange={(v) => set('TradeLicenceNo', v)} disabled={isView} testId="ent-tradelicense" />
                <S label="TRN Status" value={form.TRNSTATUS} onChange={(v) => set('TRNSTATUS', v)} disabled={isView} testId="ent-trnstatus"
                  options={[{ value: 'YES', label: 'YES' }, { value: 'NO', label: 'NO' }]} />
                <F label="TRN No" value={form.TRNNo} onChange={(v) => set('TRNNo', v)} disabled={isView} testId="ent-trnno" />
              </div>
              <div>
                <S label="Status" value={form.Status} onChange={(v) => set('Status', v)} disabled={isView || !isEdit} testId="ent-status"
                  options={[{ value: 'Active', label: 'Active' }, { value: 'InActive', label: 'InActive' }]} />
                <S label="Cloud Status" value={form.CStatus} onChange={(v) => set('CStatus', v)} disabled={isView || !isEdit} testId="ent-cstatus"
                  options={[{ value: 'Live', label: 'Live' }, { value: 'Pending', label: 'Pending' }]} />
                <F label="No.Of Devices" value={form.NoOfDevices} onChange={(v) => set('NoOfDevices', v)} type="number" disabled={isView} testId="ent-devices" />
              </div>
            </div>
          </div></div>
        )}
        {tab === 'address' && (
          <div className="erp-form-card"><div className="erp-form-section">
            <div className="erp-form-3col">
              <div>
                <F label="Phone No 1" value={form.PhoneNo1} onChange={(v) => set('PhoneNo1', v)} required disabled={isView} testId="ent-phone1" />
                <F label="Phone No 2" value={form.PhoneNo2} onChange={(v) => set('PhoneNo2', v)} disabled={isView} testId="ent-phone2" />
                <F label="Address" value={form.Address1} onChange={(v) => set('Address1', v)} disabled={isView} testId="ent-address" />
                <F label="Pincode" value={form.Pincode} onChange={(v) => set('Pincode', v)} disabled={isView} testId="ent-pincode" />
              </div>
              <div>
                <S label="Country" value={form.Country} onChange={(v) => { set('Country', v); set('State', ''); set('City', ''); }} required disabled={isView} testId="ent-country"
                  options={countries.map(c => ({ value: c.sortname, label: c.name }))} />
                <S label="State" value={form.State} onChange={(v) => { set('State', v); set('City', ''); }} disabled={isView} testId="ent-state"
                  options={states.map(s => ({ value: s.StateId, label: s.StateName }))} />
                <S label="City" value={form.City} onChange={(v) => set('City', v)} disabled={isView} testId="ent-city"
                  options={cities.map(c => ({ value: c.ID, label: c.Name }))} />
              </div>
              <div>
                <S label="Currency" value={form.Currency} onChange={(v) => set('Currency', v)} disabled={isView} testId="ent-currency"
                  options={currencies.map(c => ({ value: String(c.CurrencyId), label: `${c.CurrencyName} (${c.CurrencySymbol})` }))} />
              </div>
            </div>
          </div></div>
        )}
        {tab === 'sql' && (
          <div className="erp-form-card"><div className="erp-form-section">
            <p style={{ fontSize: 11, color: 'var(--erp-text-muted)', marginBottom: 12 }}>SQL Server connection for this enterprise's dedicated database</p>
            <div className="erp-form-2col">
              <div>
                <F label="SQL Server ID" value={form.SQLServerId} onChange={(v) => set('SQLServerId', v)} disabled={isView} testId="ent-sqlserver" />
                <F label="SQL Database Name" value={form.SQLDBName} onChange={(v) => set('SQLDBName', v)} disabled={isView} testId="ent-sqldb" />
                <F label="SQL Schema Name" value={form.SQLSchemaName} onChange={(v) => set('SQLSchemaName', v)} disabled={isView} testId="ent-sqlschema" />
              </div>
              <div>
                <F label="SQL User Name" value={form.SQLUserName} onChange={(v) => set('SQLUserName', v)} disabled={isView} testId="ent-sqluser" />
                <F label="SQL Password" value={form.SQLPassword} onChange={(v) => set('SQLPassword', v)} disabled={isView} testId="ent-sqlpwd" type="password" />
                <div style={{ padding: '8px 0', fontSize: 10, color: 'var(--erp-text-muted)' }}>
                  {form.DataBaseCreated ? '✅ Database Created' : '⏳ Database Not Created'}
                </div>
              </div>
            </div>
          </div></div>
        )}
        {tab === 'subscription' && (
          <div className="erp-form-card"><div className="erp-form-section">
            <div className="erp-form-2col">
              <div>
                <S label="Package" value={form.Packages} onChange={(v) => set('Packages', v)} disabled={isView} testId="ent-package"
                  options={packages.map(p => ({ value: String(p.Pid), label: `${p.packageName} (${p.packageduration} days) - ${p.Price}` }))} />
                <S label="Payment Gateway" value={form.PaymentGateway} onChange={(v) => set('PaymentGateway', v)} disabled={isView} testId="ent-gateway"
                  options={[{ value: '', label: 'None' }, { value: 'Razorpay', label: 'Razorpay' }, { value: 'PayU', label: 'PayU' }, { value: 'Stripe', label: 'Stripe' }]} />
                <S label="Payment Status" value={form.paymentStatus} onChange={(v) => set('paymentStatus', v)} disabled={isView} testId="ent-paystatus"
                  options={[{ value: 'I', label: 'Initiated' }, { value: 'T', label: 'Trial' }, { value: 'P', label: 'Paid' }, { value: 'E', label: 'Expired' }]} />
              </div>
              <div>
                <F label="Payment Amount" value={form.paymentAmount} onChange={(v) => set('paymentAmount', v)} disabled={isView} testId="ent-payamount" />
              </div>
            </div>
          </div></div>
        )}
        {tab === 'users' && (
          <div className="erp-form-card"><div className="erp-form-section">
            <p style={{ fontSize: 11, color: 'var(--erp-text-muted)', marginBottom: 12 }}>
              Users registered under this enterprise (ClientMaster_Child)
            </p>
            <div className="erp-grid-wrapper">
              <table className="erp-grid">
                <thead>
                  <tr><th>SlNo</th><th>Client ID</th><th>User Name</th><th>Email</th><th>User Type</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {childUsers.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: 'var(--erp-text-muted)' }}>{form.ClientId ? 'No users found' : 'Save enterprise first to see users'}</td></tr>
                  ) : childUsers.map(u => (
                    <tr key={u.SlNo}>
                      <td>{u.SlNo}</td>
                      <td className="cell-mono">{u.ClientId}</td>
                      <td className="cell-bold">{u.ClientName}</td>
                      <td>{u.EmailId}</td>
                      <td>{u.UserType === 'M' ? 'Master' : u.UserType === 'S' ? 'SSAdmin' : u.UserType === 'C' ? 'Child' : u.UserType}</td>
                      <td><span className={`erp-status-badge ${u.Status === 'Active' ? 'active' : 'inactive'}`}>{u.Status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div></div>
        )}
        <DynamicFieldRenderer screenName="EnterpriseAccount" recordId={form.ClientId} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />
      </form>
    </div>
  );
};

// ═══════════════════════ MAIN ═══════════════════════
const EnterpriseAccountScreen = ({ theme = 'light', onBack }) => {
  const [view, setView] = useState('grid');
  const [editData, setEditData] = useState(null);
  const [formMode, setFormMode] = useState('add');
  const token = getToken() || '';

  if (view === 'form') {
    return <EnterpriseForm token={token} editData={editData} mode={formMode}
      onGridView={() => { setView('grid'); setEditData(null); }}
      onSaved={() => { setView('grid'); setEditData(null); }} />;
  }
  return <EnterpriseGrid token={token}
    onAddNew={() => { setEditData(null); setFormMode('add'); setView('form'); }}
    onEdit={(row, mode) => { setEditData(row); setFormMode(mode || 'edit'); setView('form'); }} />;
};

export default EnterpriseAccountScreen;
